import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableEmpty,
  TableSkeleton,
} from "@aisquare/ui";
import * as React from "react";
import { Section, VariantBlock } from "../lib/Section";

const rows = [
  { id: "001", primitive: "Button", since: "v0.1.0", consumers: 4 },
  { id: "002", primitive: "Dialog", since: "v0.1.0", consumers: 3 },
  { id: "003", primitive: "Popover", since: "v0.2.0", consumers: 2 },
  { id: "004", primitive: "Tabs", since: "v0.2.0", consumers: 5 },
];

export function TableDemo() {
  return (
    <Section
      id="table"
      title="Table"
      description="Data table with auto x-scroll wrapper. Hover and selected states wired in."
    >
      <VariantBlock label="Standard table">
        <Table>
          <TableCaption>Primitive adoption snapshot</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Primitive</TableHead>
              <TableHead>Since</TableHead>
              <TableHead className="text-right">Consumers</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-mono text-xs text-muted-foreground">{row.id}</TableCell>
                <TableCell>{row.primitive}</TableCell>
                <TableCell>{row.since}</TableCell>
                <TableCell className="text-right">{row.consumers}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3}>Total</TableCell>
              <TableCell className="text-right">
                {rows.reduce((sum, r) => sum + r.consumers, 0)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </VariantBlock>

      <VariantBlock label="Selected row state">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item</TableHead>
              <TableHead>State</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>Normal row</TableCell>
              <TableCell>—</TableCell>
            </TableRow>
            <TableRow data-state="selected">
              <TableCell>Selected row</TableCell>
              <TableCell>data-state=selected</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </VariantBlock>

      <VariantBlock
        label="TableSkeleton / TableEmpty"
        caption="both render real <tr>s meant to sit inside <TableBody>, so the table keeps ONE column model and the browser keeps one table layout. They live in the composed tier, not in ui/table.tsx, because table is on the shadcn re-import allowlist and anything added there is overwritten on the next pull"
      >
        <TableStates />
      </VariantBlock>
    </Section>
  );
}

const INVOICES = [
  { id: "INV-001", plan: "Studio Pro", period: "Aug 2026", amount: "$48.00" },
  { id: "INV-002", plan: "Studio Pro", period: "Jul 2026", amount: "$48.00" },
  { id: "INV-003", plan: "Studio Pro", period: "Jun 2026", amount: "$48.00" },
];

/**
 * The branch order is the whole lesson: `loading` is tested BEFORE the length
 * check, because a table that has not loaded and a table with no rows are the
 * same `[]`, and "No invoices yet." over a request still in flight is wrong.
 */
function TableStates() {
  const [phase, setPhase] = React.useState<"loading" | "empty" | "loaded">("loading");
  const rows = phase === "loaded" ? INVOICES : [];

  return (
    <div className="w-full max-w-2xl">
      <div className="mb-3 flex gap-2">
        {(["loading", "empty", "loaded"] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPhase(p)}
            className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground data-[active=true]:border-primary data-[active=true]:text-primary"
            data-active={phase === p}
          >
            {p}
          </button>
        ))}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Invoice</TableHead>
            <TableHead>Plan</TableHead>
            <TableHead>Period</TableHead>
            <TableHead className="text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {phase === "loading" ? (
            <TableSkeleton columns={4} rows={3} />
          ) : rows.length === 0 ? (
            <TableEmpty colSpan={4}>No invoices yet.</TableEmpty>
          ) : (
            rows.map((inv) => (
              <TableRow key={inv.id}>
                <TableCell className="font-mono text-xs">{inv.id}</TableCell>
                <TableCell>{inv.plan}</TableCell>
                <TableCell>{inv.period}</TableCell>
                <TableCell className="text-right">{inv.amount}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
